import Link from "next/link";
import { useRouter } from "next/router";
import clsx from "clsx";

import { BRAND_PATHS, FEATURE_FLAGS } from "@chef/constants";
import {
  Breakfast,
  Calendar,
  Dinner,
  ReceiptDefault,
  User,
} from "@chef/icons/small";
import { Cart } from "@chef/icons/large";
import { useTrack } from "@chef/state-management/hooks";
import { DeviationIndicator } from "@chef/smart-components";
import { isEqualStrings } from "@chef/utils/equal";
import { useFlag } from "@chef/feature-flags";
import { getNextCutoff } from "@chef/helpers";

import type { WeekYear } from "@chef/types/common";

import { intl } from "./TabBar.Intl";

interface LinkWithIconProps {
  id: string;
  label: string;
  href: string | ((args: WeekYear) => string);
  week: number;
  year: number;
  activePaths: ActivePaths;
  children: React.ReactNode;
  badge?: React.FunctionComponent<WeekYear & { variant: "tab" }>;
}

type ActivePath = string | ((args: WeekYear) => string);

type ActivePaths = (ActivePath | [ActivePath, "exact" | "includes"])[];

type TabBarItems = {
  view: "default" | "my-menu";
  id: string;
  label: string;
  href: string | ((args: WeekYear) => string);
  activePaths: ActivePaths;
  icon: React.ReactNode;
  badge?: React.FunctionComponent<WeekYear & { variant: "tab" }>;
}[];

const items: TabBarItems = [
  {
    id: "bottom_tab_bar_dishes",
    label: intl.MEALS,
    href: BRAND_PATHS.MY_MENU_WEEK_HREF,
    activePaths: [
      [BRAND_PATHS.MY_MENU_WEEK_HREF, "exact"],
      [BRAND_PATHS.MY_MENU_PAGE, "exact"],
    ],
    icon: <Dinner className="w-6 h-6" />,
    view: "my-menu",
  },
  {
    id: "bottom_tab_bar_addons",
    label: intl.ADDONS,
    href: BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE,
    activePaths: [BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE],
    icon: <Breakfast className="w-6 h-6" />,
    view: "my-menu",
  },
  {
    id: "bottom_tab_bar_checkout",
    label: intl.ORDER,
    href: BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE,
    activePaths: [
      BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE,
      BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE,
      BRAND_PATHS.WEEK_EDITOR_ADD_MORE_PAGE,
    ],
    icon: <Cart className="w-6 h-6" />,
    badge: DeviationIndicator,
    view: "my-menu",
  },
  {
    id: "bottom_tab_bar_plan",
    label: intl.PLAN,
    href: BRAND_PATHS.MY_MENU_PAGE,
    activePaths: [BRAND_PATHS.MY_MENU_PAGE],
    icon: <Dinner className="w-6 h-6" />,
    view: "default",
  },
  {
    id: "bottom_tab_bar_calendar",
    label: intl.CALENDAR,
    href: BRAND_PATHS.DELIVERY_CALENDAR_UPCOMING_LINK,
    activePaths: [BRAND_PATHS.DELIVERY_CALENDAR_UPCOMING_LINK],
    icon: <Calendar className="w-6 h-6" />,
    view: "default",
  },
  {
    id: "bottom_tab_bar_recipes",
    label: intl.RECIPES,
    href: BRAND_PATHS.MY_RECIPES_PAGE,
    activePaths: [BRAND_PATHS.MY_RECIPES_PAGE],
    icon: <ReceiptDefault className="w-8 h-8 -m-1" />,
    view: "default",
  },
  {
    id: "bottom_tab_bar_account",
    label: intl.ACCOUNT,
    href: BRAND_PATHS.MY_ACCOUNT_HREF,
    activePaths: [BRAND_PATHS.MY_ACCOUNT_HREF],
    icon: <User className="w-10 h-10 -m-2" />,
    view: "default",
  },
  // {
  //   id: "bottom_tab_bar_customer_service",
  //   label: intl.CUSTOMER_SERVICE,
  //   href: BRAND_PATHS.CONTACT_US_PAGE,
  //   activePaths: [BRAND_PATHS.CONTACT_US_PAGE],
  //   icon: <Complaint className="w-6 h-6" />,
  //   view: "default",
  // },
];

export const LinkWithIcon = ({
  id,
  label,
  href,
  week,
  year,
  activePaths,
  children,
  badge: Badge,
}: LinkWithIconProps) => {
  const router = useRouter();
  const track = useTrack();

  const handleClick = (label: string, href: string) => {
    track("linkClicked", {
      affiliation: "Frontend process",
      link_action: "click",
      link_destination: href,
      link_placement: "bottom_tab_bar",
      link_source: "code",
      link_text: label,
    });
  };

  const _href = typeof href === "function" ? href({ week, year }) : href;

  const isActive = activePaths.some((path) => {
    let p: string | ((args: WeekYear) => string),
      type: "exact" | "includes" = "includes";

    if (Array.isArray(path)) {
      [p, type] = path;
    } else {
      p = path;
    }

    const _path = typeof p === "function" ? p({ week, year }) : p;

    if (type === "includes") {
      return router.asPath.includes(_path);
    } else {
      return isEqualStrings(router.asPath, _path);
    }
  });

  return (
    <Link
      key={label}
      href={_href}
      passHref
      className="relative text-center grow"
      id={id}
      onClick={() => handleClick(label, _href)}
    >
      {isActive && (
        <div className="absolute left-0 right-0 z-0 bg-grey-2/85 rounded-full top-0.75 bottom-0.75" />
      )}

      <div
        className={clsx(
          "z-10 relative py-2 items-center justify-center flex flex-col",
        )}
      >
        <div className="relative">
          {Badge && (
            <span className="absolute z-20 pointer-events-none -top-1 -right-2">
              <Badge week={week} year={year} variant="tab" />
            </span>
          )}
          <span className={clsx(isActive ? "opacity-100" : "opacity-75")}>
            {children}
          </span>
        </div>
        <div className={clsx("text-xs", isActive && "font-bold")}>{label}</div>
      </div>
    </Link>
  );
};

export const TabBar = () => {
  const router = useRouter();
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const variant = isWeekEditorRedesignEnabled ? "my-menu" : "default";

  const { week: _week, year: _year } = router.query;

  const { week: nextCutoffWeek, year: nextCutoffYear } = getNextCutoff();

  const week = _week ? Number(_week) : nextCutoffWeek;
  const year = _year ? Number(_year) : nextCutoffYear;

  const _items = items.filter((i) => i.view === variant);

  return (
    <div className="px-0.75">
      <nav
        className="flex text-center"
        id="tab-bar"
        aria-label="Bottom tab bar"
      >
        {_items.map(({ label, href, activePaths, icon, id, badge }) => (
          <LinkWithIcon
            week={week}
            year={year}
            id={id}
            key={id}
            label={label}
            href={href}
            activePaths={activePaths}
            badge={badge}
          >
            {icon}
          </LinkWithIcon>
        ))}
      </nav>
    </div>
  );
};
